import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Trophy, QrCode, Download, Copy, Loader2, TrendingUp, Info,
  CheckCircle2, AlertCircle,
} from "lucide-react";

/**
 * Vendor Success Score gauge + QR code panel. Shown on the vendor dashboard
 * so every vendor sees:
 *   • their current score, grade, and the 7 components that drive it
 *   • their QR code — download, copy the link, print for their booth
 */
export default function VendorScorecardCard() {
  const [score, setScore] = useState(null);
  const [qr, setQr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, q] = await Promise.allSettled([
          api.get("/vendor/success-score"),
          api.get("/vendor/qr-code"),
        ]);
        if (s.status === "fulfilled") setScore(s.value.data);
        if (q.status === "fulfilled") setQr(q.value.data);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="mt-8 card-warm rounded-lg p-8 text-center"
            style={{ backgroundColor: "#fff" }}
            data-testid="scorecard-loading">
        <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading your scorecard…
      </div>
    );
  }

  return (
    <div className="mt-8 grid gap-4 lg:grid-cols-3" data-testid="vendor-scorecard">
      <ScoreCard score={score} />
      <QrCard qr={qr} />
    </div>
  );
}

function ScoreCard({ score }) {
  if (!score) return null;
  const gradeColor = score.score >= 70 ? "var(--forest)"
    : score.score >= 40 ? "var(--gold)" : "var(--terracotta)";
  return (
    <div
      data-testid="success-score-card"
      className="lg:col-span-2 card-warm rounded-lg p-6"
      style={{ backgroundColor: "#fff", borderLeft: `3px solid ${gradeColor}` }}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline inline-flex items-center gap-1.5" style={{ color: gradeColor }}>
            <Trophy className="h-3 w-3" /> Success Score
          </div>
          <div className="mt-2 flex items-baseline gap-3">
            <div className="font-serif text-6xl" data-testid="success-score-value"
                  style={{ color: gradeColor }}>
              {score.score}
            </div>
            <div className="text-sm" style={{ color: "var(--ink-soft)" }}>/ 100</div>
          </div>
          <div className="text-sm mt-1 capitalize" data-testid="success-score-grade"
                style={{ color: "var(--ink-soft)" }}>
            {score.grade}
          </div>
        </div>
        <div className="text-xs max-w-xs text-right" style={{ color: "var(--ink-soft)" }}>
          Your score updates every time you view this dashboard. Higher scores get better placement in event matchmaking and organizer inboxes.
        </div>
      </div>

      <div className="mt-6 space-y-2.5" data-testid="score-breakdown">
        {score.breakdown.map((c) => {
          const pct = Math.round((c.earned / c.weight) * 100);
          const good = pct >= 80;
          return (
            <div
              key={c.key}
              data-testid={`score-row-${c.key}`}
              className="rounded-md p-3"
              style={{
                border: "1px solid rgba(0,0,0,0.06)",
                backgroundColor: "#fbf8f2",
              }}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  {good
                    ? <CheckCircle2 className="h-4 w-4 shrink-0" style={{ color: "var(--forest)" }} />
                    : <AlertCircle className="h-4 w-4 shrink-0" style={{ color: "var(--gold)" }} />}
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{c.label}</div>
                    <div className="text-xs" style={{ color: "var(--ink-soft)" }}>{c.detail}</div>
                  </div>
                </div>
                <div className="text-sm font-mono whitespace-nowrap" style={{ color: good ? "var(--forest)" : "var(--ink-soft)" }}>
                  {c.earned}<span className="opacity-50">/{c.weight}</span>
                </div>
              </div>
              {/* Progress bar */}
              <div className="mt-2 h-1.5 rounded-full overflow-hidden"
                    style={{ backgroundColor: "rgba(0,0,0,0.06)" }}>
                <div
                  className="h-full transition-all duration-500"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: good ? "var(--forest)" : "var(--gold)",
                  }}
                />
              </div>
              {c.tip && (
                <div className="mt-2 text-xs flex items-start gap-1.5"
                      style={{ color: "var(--terracotta)" }}
                      data-testid={`score-tip-${c.key}`}>
                  <Info className="h-3 w-3 shrink-0 mt-0.5" />
                  <span>{c.tip}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function QrCard({ qr }) {
  const [copying, setCopying] = useState(false);
  if (!qr) {
    return (
      <div data-testid="qr-card-empty" className="card-warm rounded-lg p-6 text-center"
            style={{ backgroundColor: "#fff" }}>
        <QrCode className="h-6 w-6 mx-auto opacity-40" />
        <div className="text-sm mt-2" style={{ color: "var(--ink-soft)" }}>
          Set a public slug for your profile to unlock your QR code.
        </div>
      </div>
    );
  }
  const copyLink = async () => {
    setCopying(true);
    try {
      await navigator.clipboard.writeText(qr.rate_url);
      toast.success("Link copied.");
    } catch {
      toast.error("Copy failed — long-press to select instead.");
    } finally { setCopying(false); }
  };
  const downloadPng = () => {
    const a = document.createElement("a");
    a.href = qr.image_data_url;
    a.download = `addvendtour-qr-${(qr.vendor_name || "vendor").replace(/\s+/g, "-").toLowerCase()}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };
  return (
    <div data-testid="qr-code-card"
          className="card-warm rounded-lg p-6"
          style={{ backgroundColor: "#fff", borderLeft: "3px solid var(--gold)" }}>
      <div className="overline inline-flex items-center gap-1.5" style={{ color: "var(--gold)" }}>
        <QrCode className="h-3 w-3" /> Your QR code
      </div>
      <div className="mt-3 flex items-center justify-center rounded-md p-3"
            style={{ backgroundColor: "#fbf8f2" }}>
        <img src={qr.image_data_url} alt="Your QR code"
              className="w-40 h-40" data-testid="qr-code-image" />
      </div>
      <div className="mt-3 text-xs break-all"
            data-testid="qr-code-url"
            style={{ color: "var(--ink-soft)" }}>
        {qr.rate_url}
      </div>
      <div className="mt-3 flex gap-2">
        <Button size="sm" variant="outline" className="flex-1 rounded-full"
                 onClick={copyLink} disabled={copying}
                 data-testid="qr-copy-link">
          <Copy className="h-3.5 w-3.5 mr-1" /> Copy link
        </Button>
        <Button size="sm" className="flex-1 rounded-full btn-primary"
                 onClick={downloadPng}
                 data-testid="qr-download">
          <Download className="h-3.5 w-3.5 mr-1" /> Download
        </Button>
      </div>
      <div className="mt-3 text-xs" style={{ color: "var(--ink-soft)" }}>
        Print and prop at your booth — scanning opens your full profile
        (photos, upcoming events, reviews, and a Book Now button). Every
        review boosts your Success Score.
      </div>
    </div>
  );
}
